import React, { useState } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Content } from '../../components/light/Content';
import { SectionBlock } from '../../components/light/SectionBlock';
import { DateTime } from '../../libs/DateTime';
import { Loading } from '../../components/Loading';
import downloadExcelFile from '../../libs/downloadExcelFile';

const QUERY = gql`
  query CustomerDeposit($asAt: String!) {
    customerDeposit(asAt: $asAt) {
      customer {
        id
        phoneNumber
      }
      balance
      lastTrxDate
    }
  }
`;

interface CustomerDepositItem {
  customer: {
    id: number;
    phoneNumber: string;
  };
  balance: number;
  lastTrxDate: string;
}

interface QueryResult {
  customerDeposit: CustomerDepositItem[];
}

export function CustomerDepositScreen(): React.ReactElement {
  const [asAt, setAsAt] = useState(DateTime.today());

  const [fetchData, { loading, data, error }] = useLazyQuery<QueryResult>(QUERY, {
    variables: { asAt },
  });

  const handleExportExcel = () => {
    if (!data) return;
    const rows = [
      ['Phone Number', 'Balance', 'Last Transaction Date'],
      ...data.customerDeposit.map(item => [item.customer.phoneNumber, item.balance.toString(), item.lastTrxDate]),
    ];
    downloadExcelFile('Customer_Deposit.xlsx', 'Customer Deposit', rows);
  };

  return (
    <Content>
      <SectionBlock title="Customer Deposit">
        <div className="controls-above-table">
          <form
            onSubmit={e => {
              e.preventDefault();
              fetchData();
            }}
          >
            <div className="mb-1 d-flex justify-content-between">
              <div>
                <div style={{ display: 'inline-block', width: 60 }}>As At</div>
                <input
                  type="date"
                  name="asAt"
                  className="lf-form-control lf-inline"
                  value={asAt}
                  onChange={e => setAsAt(e.target.value)}
                />
                <button type="submit" className="lf-btn btn-primary ml-1">
                  Load
                </button>
              </div>
              <div>
                {data && (
                  <button type="button" className="lf-btn btn-success" onClick={handleExportExcel}>
                    Export to Excel
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {loading && <Loading />}
        {error && <div className="alert alert-danger">{error.message}</div>}

        {data && (
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th className="text-right">Balance</th>
                <th>Last Transaction Date</th>
              </tr>
            </thead>
            <tbody>
              {data.customerDeposit.map((item, index) => (
                <tr key={index}>
                  <td>
                    <Link to={`/audit/customer-deposit-statement/${item.customer.id}?asAt=${asAt}`} target="_blank">
                      {item.customer.phoneNumber}
                    </Link>
                  </td>
                  <td className="text-right">{item.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td>{item.lastTrxDate}</td>
                </tr>
              ))}
              {data.customerDeposit.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </SectionBlock>
    </Content>
  );
}
